import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import {
  Language,
  TranslationKey,
  translations,
  detectLanguage,
} from "../lib/translations";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, replacements?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("language") as Language;
        if (saved && (saved === "en" || saved === "fi" || saved === "sv")) {
          return saved;
        }
      } catch {
        // localStorage may be unavailable
      }
      return detectLanguage();
    }
    return "en";
  });

  useEffect(() => {
    try {
      localStorage.setItem("language", language);
    } catch {
      // localStorage may be unavailable
    }
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key: TranslationKey, replacements?: Record<string, string>): string => {
      let text = translations[language][key];
      if (replacements) {
        Object.entries(replacements).forEach(([k, v]) => {
          text = text.replace(`{${k}}`, v);
        });
      }
      return text;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
